import { cn } from "@/lib/utils";
import type { PromptCollection } from "@/lib/types";
import { MoreHorizontal, Globe, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface CollectionCardProps {
  collection: PromptCollection;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isOwner?: boolean;
}

export function CollectionCard({ 
  collection, 
  onClick, 
  onEdit, 
  onDelete,
  isOwner = false 
}: CollectionCardProps) {
  return (
    <div
      className={cn(
        "group relative bg-card border border-border rounded-xl p-4 transition-all hover:border-primary/30 hover:shadow-md",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{collection.emoji}</span>
          <div>
            <h3 className="font-medium text-foreground line-clamp-1">{collection.name}</h3>
            <p className="text-xs text-muted-foreground">
              {collection.promptCount || 0} prompt{collection.promptCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={onEdit}>Edit Collection</DropdownMenuItem>
              <DropdownMenuItem 
                onClick={onDelete}
                className="text-destructive"
              >
                Delete Collection
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Description */}
      {collection.description && (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {collection.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {collection.isPublic ? (
          <span className="flex items-center gap-1">
            <Globe className="h-3 w-3" />
            Public
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <Lock className="h-3 w-3" />
            Private
          </span>
        )}
      </div>
    </div>
  );
}
